import { TournamentRankings, TournamentRanking } from '@/model/ranking/TournamentRankings'
import { sliceAndSortRankings, formatWinnings } from '@/store/modules/ranking/index'
import { SortOrder } from '@/model/ranking/Sorting'
import { State } from '@/store/state'
import { ERROR_CODES } from '@/model/ErrorCodes'

export interface TournamentRankingState {
  rankings: Array<TournamentRanking>
  sorting: SortOrder
  prevBestScore: number
}

const state: TournamentRankingState = {
  rankings: [],
  sorting: SortOrder.DESCENDING,
  prevBestScore: null,
}

export const getters = {
  // TODO: Note the order of sorting and slicing,
  // i.e. array gets sliced first and sorted afterwards (performance reasons)
  // so we assume the backend already sends rankings sorted properly
  rankings:
    (state: TournamentRankingState, getters: any, rootState: State) =>
    (from: number, to?: number): Array<TournamentRanking> => {
      const rankings = sliceAndSortRankings(
        [...state.rankings],
        from,
        to,
        sortTournamentRankings(state.sorting)
      ) as Array<TournamentRanking>

      const replaced = rankings.map((ranking) => ({
        ...ranking,
        tournamentRank: ranking.tournamentRank > 0 ? ranking.tournamentRank : '-',
      })) as Array<TournamentRanking>

      return formatWinnings(replaced, rootState.user.currency) as Array<TournamentRanking>
    },
  usersByName:
    (state: TournamentRankingState, getters: any, rootState: State) =>
    (term: string, from = 0, to?: number) => {
      try {
        const search = new RegExp(term.toLowerCase())
        const rankings = state.rankings
          .filter((item) => search.test(item.playerName.toLowerCase()))
          .sort(sortTournamentRankings(state.sorting))
          .slice(from, to)
          .map((ranking) => ({
            ...ranking,
            tournamentRank: ranking.tournamentRank > 0 ? ranking.tournamentRank : '-',
          })) as Array<TournamentRanking>
        return formatWinnings(rankings, rootState.user.currency)
      } catch (err) {
        return []
      }
    },
}

const actions = {
  fetch({ commit, getters }: any, tournamentId: string) {
    // TODO add cache to not load always the same data
    return new Promise((res: (value?: unknown) => void, rej: (reason?: unknown) => void) => {
      fetch(`${process.env.VUE_APP_REST_URL}ranking/tournament/${tournamentId}`, {
        credentials: 'same-origin',
        cache: 'no-cache',
      })
        .then((res: Response) => {
          if (res.status >= 400) {
            commit('error', { id: 'ranking/tournament/fetch', responseStatus: res.status }, { root: true })
            throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.UNKNOWN }
          }
          return res.json()
        })
        .then((data: TournamentRankings) => {
          commit('rankings', data.tournamentRankings)
          commit('prevBestScore', data.prevBestScore)
          res()
        })
        .catch((err) => {
          rej(err)
        })
    })
  },
}

const mutations = {
  rankings(state: TournamentRankingState, list: Array<TournamentRanking>) {
    state.rankings = list
  },
  prevBestScore(state: TournamentRankingState, prevBestScore: number) {
    state.prevBestScore = prevBestScore
  },
  sorting(state: TournamentRankingState, order: SortOrder) {
    state.sorting = order
  },
}

const sortTournamentRankings = (sorting: SortOrder) => (a: TournamentRanking, b: TournamentRanking) => {
  if (a.tournamentRank === 0) {
    return 1
  }
  if (b.tournamentRank === 0) {
    return -1
  }
  switch (sorting) {
    case SortOrder.ASCENDING:
      return parseFloat(b.totalWin) < parseFloat(a.totalWin) || b.tournamentRank > a.tournamentRank ? 1 : -1
    case SortOrder.DESCENDING:
      return parseFloat(b.totalWin) > parseFloat(a.totalWin) || b.tournamentRank < a.tournamentRank ? 1 : -1
    default:
      return b.tournamentRank < a.tournamentRank ? 1 : -1
  }
}

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}
